import { useCallback, useEffect, useMemo, useRef } from 'react'
import { toast } from 'react-common'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  FilterDrawerType,
  IFilter,
  PriorityFilterValue,
  ProgressFilterValue,
} from '@/components/CourseFiltersDrawer/types'
import { useGetTermsQuery } from '@/store/api/termsApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { createFiltersSelector, setFilters } from '@/store/slices/aggregatesSlice'
import { useCollectionFilters } from './useCollectionFilters/useCollectionFilters'
import { useFilterCategories } from './useFilterCategories/useFilterCategories'
import { usePublicCollectionFilters } from './usePublicCollectionFilters/usePublicCollectionFilters'
import { usePublicFilterCategories } from './usePublicFilterCategories/usePublicFilterCategories'

export const SEARCH_INPUT_PARAM = 'keyword'
const allowedFilters: (keyof IFilter)[] = ['collection', 'term', 'courseProgress', 'coursePriority', 'category']
const defaultFilters: IFilter = { courseProgress: [], coursePriority: [], term: [], collection: [], category: {} }
const allowedProgressFilters: ProgressFilterValue[] = [
  ProgressFilterValue.COMPLETED,
  ProgressFilterValue.IN_PROGRESS,
  ProgressFilterValue.NOT_STARTED,
]
const allowedPriorityFilters: PriorityFilterValue[] = [PriorityFilterValue.MANDATORY, PriorityFilterValue.OPTIONAL]

const usePreselectFilters = (type: FilterDrawerType) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const selectCategoryHandled = useRef(false)

  const getCollectionFilters = type === 'public' ? usePublicCollectionFilters : useCollectionFilters
  const { isLoading: isCollectionsLoading, collectionFilters } = getCollectionFilters(type)

  const getFilterCategories = type === 'public' ? usePublicFilterCategories : useFilterCategories
  const { filterCategories, isLoading: isFilterCategoriesLoading } = getFilterCategories(type)

  const termIds = useAppSelector((state) => state.aggregates.termIds)

  const { data: termsData, isLoading: isTermsLoading } = useGetTermsQuery(undefined, { skip: type === 'public' })

  const termOptions = useMemo(
    () => (isTermsLoading ? [] : (termsData || []).filter((term) => termIds.includes(term.id))),
    [isTermsLoading, termIds, termsData],
  )
  const storeFilters = useAppSelector((state) => createFiltersSelector(state, type))
  const storeKeywordText = useAppSelector((state) =>
    type === 'enrolled' ? state.aggregates.enrolledCoursesSearchText : state.aggregates.catalogCoursesSearchText,
  )

  const parseInputSearchParam = useCallback(() => {
    const params = new URLSearchParams(location.search)

    // If there is no keyword in url, apply keyword from the store
    if (!location.search) {
      params.set(SEARCH_INPUT_PARAM, storeKeywordText)
    }

    return params.get(SEARCH_INPUT_PARAM)
  }, [location.search, storeKeywordText])

  const parseQueryParams = useCallback(() => {
    const params = new URLSearchParams(location.search)

    params.delete(SEARCH_INPUT_PARAM)

    // If there are no search filters, apply filters from the store
    if (!location.search) {
      Object.entries(storeFilters).forEach(([key, values]) => {
        if (key === 'category') {
          // Handle category filters specially - include ALL categories (even empty ones) to preserve cleared state
          Object.entries(values as Record<string, string[]>).forEach(([categoryId, attributes]) => {
            if (attributes.length > 0) {
              params.set(`category[${categoryId}]`, attributes.join(','))
            }
          })
        } else if ((values as string[]).length > 0) {
          params.set(key, (values as string[]).join(','))
        }
      })
    }

    const parsedParams: IFilter = { ...defaultFilters }

    if (!location.search) {
      parsedParams.category = { ...storeFilters.category }
    }

    let hasIvalidParams = false

    params.forEach((value, key) => {
      // Handle category filters with format "category[categoryId]"
      if (key.startsWith('category[') && key.endsWith(']')) {
        const categoryId = key.slice(9, -1) // Extract categoryId from "category[categoryId]"
        parsedParams.category[categoryId] = value.split(',')
        return
      }

      const k = key as keyof IFilter
      if (!allowedFilters.includes(k)) {
        params.delete(key)
        hasIvalidParams = true
      } else {
        switch (k) {
          case 'coursePriority':
            parsedParams.coursePriority = value.split(',') as PriorityFilterValue[]
            break
          case 'courseProgress':
            parsedParams.courseProgress = value.split(',') as ProgressFilterValue[]
            break
          case 'category':
            // Category filters are handled above
            break
          default:
            parsedParams[k] = value.split(',') as string[]
            break
        }
      }
    })
    return { parsedParams, hasIvalidParams }
  }, [location.search, storeFilters])

  const validateCollections = useCallback(
    (collections: string[]) => {
      if (!collections.length) {
        return { validValues: [], hasInvalidValues: false }
      }

      const validValues = collections.filter((id) => collectionFilters.some((item) => item.id === id))
      const hasInvalid = validValues.length !== collections.length

      return {
        validValues,
        hasInvalidValues: hasInvalid,
      }
    },
    [collectionFilters],
  )

  const validateTerms = useCallback(
    (terms: string[]) => {
      if (!terms.length) {
        return { validValues: [], hasInvalidValues: false }
      }

      const validValues = terms.filter((id) => termOptions.some((item) => item.id === id))
      const hasInvalid = validValues.length !== terms.length

      return {
        validValues,
        hasInvalidValues: hasInvalid,
      }
    },
    [termOptions],
  )

  const validateCategories = useCallback(
    (categories: Record<string, string[]>) => {
      if (!Object.keys(categories).length) {
        return { validValues: {}, hasInvalidValues: false }
      }

      const validValues: Record<string, string[]> = {}
      let hasInvalidValues = false

      Object.entries(categories).forEach(([categoryId, attributeIds]) => {
        const category = filterCategories.find((cat) => cat.id === categoryId)
        if (!category) {
          hasInvalidValues = true
          return
        }

        const validAttributeIds = attributeIds.filter((attrId) =>
          category.attributes?.some((attr) => attr.id === attrId),
        )

        if (validAttributeIds.length !== attributeIds.length) {
          hasInvalidValues = true
        }

        validValues[categoryId] = validAttributeIds
      })

      return { validValues, hasInvalidValues }
    },
    [filterCategories],
  )

  const validateCourseProgress = useCallback((progress: ProgressFilterValue[]) => {
    if (!progress.length) {
      return { validValues: [], hasInvalidValues: false }
    }

    const validValues = progress.filter((progressValue) =>
      allowedProgressFilters.some((item) => item === progressValue),
    )
    const hasInvalid = validValues.length !== progress.length

    return {
      validValues,
      hasInvalidValues: hasInvalid,
    }
  }, [])

  const validateCoursePriority = useCallback((priority: PriorityFilterValue[]) => {
    if (!priority.length) {
      return { validValues: [], hasInvalidValues: false }
    }

    const validValues = priority.filter((priorityValue) =>
      allowedPriorityFilters.some((item) => item === priorityValue),
    )
    const hasInvalid = validValues.length !== priority.length

    return {
      validValues,
      hasInvalidValues: hasInvalid,
    }
  }, [])

  useEffect(() => {
    if (selectCategoryHandled.current || isCollectionsLoading || isTermsLoading || isFilterCategoriesLoading) return

    const inputSearchParam = parseInputSearchParam()
    const { parsedParams: queryParams, hasIvalidParams } = parseQueryParams()

    let shouldNotify = hasIvalidParams

    if (queryParams.collection.length) {
      const { validValues, hasInvalidValues } = validateCollections(queryParams.collection)
      queryParams.collection = validValues
      shouldNotify = shouldNotify || hasInvalidValues
    }

    if (queryParams.term.length) {
      const { validValues, hasInvalidValues } = validateTerms(queryParams.term)
      queryParams.term = validValues
      shouldNotify = shouldNotify || hasInvalidValues
    }

    if (Object.keys(queryParams.category).length) {
      const { validValues, hasInvalidValues } = validateCategories(queryParams.category)
      queryParams.category = validValues
      shouldNotify = shouldNotify || hasInvalidValues
    }

    if (queryParams.courseProgress.length) {
      const { validValues, hasInvalidValues } = validateCourseProgress(queryParams.courseProgress)
      queryParams.courseProgress = validValues
      shouldNotify = shouldNotify || hasInvalidValues
    }

    if (queryParams.coursePriority.length) {
      const { validValues, hasInvalidValues } = validateCoursePriority(queryParams.coursePriority)
      queryParams.coursePriority = validValues
      shouldNotify = shouldNotify || hasInvalidValues
    }

    dispatch(
      setFilters({
        type,
        filters: queryParams,
      }),
    )

    const newParams = new URLSearchParams()
    Object.entries(queryParams).forEach(([key, values]) => {
      if (key === 'category') {
        // Handle category filters specially
        Object.entries(values as Record<string, string[]>).forEach(([categoryId, attributes]) => {
          if (attributes.length > 0) {
            newParams.set(`category[${categoryId}]`, attributes.join(','))
          }
        })
      } else if ((values as string[]).length > 0) {
        newParams.set(key, (values as string[]).join(','))
      }
    })

    if (inputSearchParam) {
      newParams.set(SEARCH_INPUT_PARAM, inputSearchParam)
    }

    const search = newParams.toString()

    if (shouldNotify && search) {
      toast(t('coursesFilterDrawer.filtersUnavailable'))
    }

    navigate({ search }, { replace: true })
    selectCategoryHandled.current = true
  }, [
    type,
    dispatch,
    navigate,
    location.search,
    termIds,
    collectionFilters,
    filterCategories,
    isCollectionsLoading,
    isTermsLoading,
    isFilterCategoriesLoading,
    termOptions,
    parseQueryParams,
    validateCollections,
    validateTerms,
    validateCategories,
    validateCourseProgress,
    validateCoursePriority,
    t,
    parseInputSearchParam,
  ])
}

export { usePreselectFilters }
